import { MuiTelInput, MuiTelInputProps } from 'mui-tel-input';

export { type TextFieldProps } from '@mui/material';

import { Skeleton } from '../Skeleton';
import {
  compactFilledBorderedInputFixedBoxSx,
  compactFilledFloatingLabelSx,
  compactFilledInputValueSx,
  compactFilledTextFieldSx,
  defaultFilledFloatingLabelSx,
  defaultFilledInputValueSx,
} from '@/lib/compactFilledFieldStyles';

type MuiPhoneInput = MuiTelInputProps & {
  loading?: boolean;
  /** Match compact entity form fields (label/input size). */
  inputCompact?: boolean;
};

export function MuiPhoneInput(props: MuiPhoneInput) {
  const {
    loading = false,
    inputCompact = false,
    slots,
    slotProps,
    ...restProps
  } = props;

  if (loading) {
    return <Skeleton variant="text" />;
  }

  if (props.variant === 'filled') {
    const inputSlot = props.slotProps?.input as { sx?: object } | undefined;
    const menuPaperSx =
      props.MenuProps?.PaperProps?.sx &&
      typeof props.MenuProps.PaperProps.sx === 'object' &&
      !Array.isArray(props.MenuProps.PaperProps.sx)
        ? props.MenuProps.PaperProps.sx
        : {};
    const flagBtnSx =
      props.FlagIconButtonProps?.sx &&
      typeof props.FlagIconButtonProps.sx === 'object' &&
      !Array.isArray(props.FlagIconButtonProps.sx)
        ? props.FlagIconButtonProps.sx
        : {};
    return (
      <MuiTelInput
        {...(restProps as any)}
        required={props.required}
        margin={
          inputCompact
            ? 'none'
            : (restProps as { margin?: MuiTelInputProps['margin'] }).margin
        }
        size={inputCompact ? 'small' : (restProps as { size?: string }).size}
        MenuProps={{
          ...props.MenuProps,
          PaperProps: {
            ...props.MenuProps?.PaperProps,
            sx: {
              bgcolor: 'background.paper',
              ...menuPaperSx,
            },
          },
        }}
        FlagIconButtonProps={{
          ...props.FlagIconButtonProps,
          sx: {
            border: 'none',
            borderRadius: '8px',
            bgcolor: 'transparent',
            backgroundColor: 'transparent',
            ...(inputCompact
              ? {
                  '&:hover': {
                    bgcolor: 'transparent',
                    backgroundColor: 'transparent',
                  },
                  '&:focus-visible': {
                    bgcolor: 'transparent',
                    backgroundColor: 'transparent',
                  },
                  '&.Mui-focusVisible': {
                    bgcolor: 'transparent',
                    backgroundColor: 'transparent',
                  },
                  '&:active': {
                    bgcolor: 'transparent',
                    backgroundColor: 'transparent',
                  },
                }
              : {
                  '&:hover': { bgcolor: 'action.hover' },
                }),
            '&.Mui-disabled': {
              opacity: 1,
              bgcolor: 'transparent',
              backgroundColor: 'transparent',
              color: 'text.disabled',
              filter: 'grayscale(1)',
            },
            ...(inputCompact && {
              boxSizing: 'border-box',
              paddingTop: '2px',
              paddingLeft: '2px',
              paddingRight: '2px',
              paddingBottom: 0,
              minWidth: 30,
              width: 30,
              height: 30,
              maxHeight: 30,
            }),
            ...flagBtnSx,
          },
        }}
        slotProps={{
          ...props.slotProps,
          inputLabel: {
            shrink: true,
            ...(props.slotProps?.inputLabel as object),
            sx: {
              ...(inputCompact
                ? compactFilledFloatingLabelSx
                : defaultFilledFloatingLabelSx),
              ...(props.slotProps?.inputLabel as { sx?: object })?.sx,
            },
          },
          input: {
            disableUnderline: true,
            ...(props.slotProps?.input as object),
            sx: {
              ...(inputCompact
                ? compactFilledInputValueSx
                : defaultFilledInputValueSx),
              ...(inputCompact ? compactFilledBorderedInputFixedBoxSx : {}),
              bgcolor: 'background.paper',
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'divider',
              ':focus-within': {
                borderColor: 'primary.main',
                bgcolor: 'background.paper',
              },
              ':has(input[aria-invalid="true"])': {
                borderColor: 'error.main',
              },
              '&.MuiFilledInput-root': {
                backgroundColor: 'transparent',
              },
              '&.MuiFilledInput-root:hover': {
                backgroundColor: 'transparent',
              },
              '&:not(.Mui-disabled):hover': {
                bgcolor: 'background.paper',
              },
              '&.MuiFilledInput-root.Mui-focused': {
                backgroundColor: 'transparent',
              },
              '&.Mui-disabled': {
                borderRadius: '8px',
                fontStyle: 'italic',
                bgcolor: 'action.disabledBackground',
                color: 'text.disabled',
                borderColor: 'divider',
                // FilledInput ::before/::after tint stacks with field bg and reads as bands
                '&::before': { display: 'none' },
                '&::after': { display: 'none' },
                '& input': {
                  color: 'text.disabled',
                  backgroundColor: 'transparent',
                  WebkitTextFillColor: 'currentColor',
                },
                '& .MuiInputAdornment-root': {
                  backgroundColor: 'transparent',
                },
                '& .MuiInputAdornment-root .MuiIconButton-root': {
                  backgroundColor: 'transparent',
                },
              },
              '&.Mui-disabled:hover': {
                bgcolor: 'action.disabledBackground',
              },
              '& .MuiInputAdornment-positionStart': {
                marginRight: inputCompact ? '2px' : undefined,
                maxHeight: inputCompact ? '100%' : undefined,
                alignSelf: 'center',
                ...(inputCompact && {
                  marginTop: 0,
                  marginBottom: 0,
                  paddingBottom: 0,
                }),
              },
              ...inputSlot?.sx,
            },
          },
        }}
        sx={[
          ...(inputCompact ? [compactFilledTextFieldSx] : []),
          {
            overflow: 'visible',
            paddingTop: inputCompact ? 0 : '1px',
            ...(inputCompact && {
              '& .MuiTelInput-IconButton': {
                paddingBottom: 0,
                marginBottom: 0,
                bgcolor: 'transparent',
                backgroundColor: 'transparent',
                backgroundImage: 'none',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: 'transparent',
                  backgroundColor: 'transparent',
                },
                '&.Mui-focusVisible': {
                  bgcolor: 'transparent',
                  backgroundColor: 'transparent',
                },
              },
            }),
            '& .MuiInputBase-root.Mui-disabled .MuiTelInput-IconButton': {
              opacity: 1,
              bgcolor: 'transparent',
              backgroundColor: 'transparent',
            },
          },
          ...(props.sx
            ? Array.isArray(props.sx)
              ? props.sx
              : [props.sx]
            : []),
        ]}
      />
    );
  }

  return (
    <MuiTelInput
      {...restProps}
      required={props.disabled ? false : props.required}
    />
  );
}
